import i from 'icepick'
import analytics from 'features/Analytics/Analytics.utils'
import { loadUserData, logoutTokenExpired, stopAutoRefresh, stopDelay } from './Auth.actions'

const initialState = {
  emailUpdatePending: false,
  updatePasswordPending: false,

  savePending: false,

  shareToken: '',
  shareTokenLoading: false,

  token: null,
  expiration: null,

  adminToken: null,
  adminUserId: null,

  user: {},
  userDataLoading: false,
  currentUsername: null,

  loggedOut: true,

  loginForm: {
    step: 1,
  },

  passwordUpdateLoading: false,

  emailConfirmationLoading: false,
  emailConfirmationDelay: {
    active: false,
    seconds: 0,
    interval: null,
  },
  emailVerifying: false,

  phoneConfirmationLoading: false,
  phoneConfirmationDelay: {
    active: false,
    seconds: 0,
    interval: null,
  },
  phoneVerifying: false,

  autoRefreshUserData: false,

  allConfirmed: true,
}

const CONFIRMATION_DELAY_SECONDS = 150

let autoRefreshTimeoutId = null

export default (state = initialState, action) => {
  switch (action.type) {
    case 'AUTH/INIT_HTTP_HEADER':
      return state

    case 'AUTH/LOAD_USER_DATA_PENDING':
      return i.assoc(state, 'userDataLoading', true)

    case 'AUTH/LOAD_USER_DATA_REJECTED':
      if (state.currentUsername) {
        action.asyncDispatch(logoutTokenExpired())
      }

      return i.assoc(state, 'userDataLoading', false)

    case 'AUTH/LOAD_USER_DATA_FULFILLED': {
      const user = action.payload.data

      // check if delay needed for confirmations
      const check = (confirmation, delayName) => {
        if (confirmation && confirmation.isSent && !confirmation.isConfirmed) {
          const now = new Date()
          const then = new Date(confirmation.sentAt)
          const diff = Math.ceil((now.getTime() - then.getTime()) / 1000)

          if (diff < CONFIRMATION_DELAY_SECONDS && diff >= 0 && !state[delayName].active) {
            action.asyncDispatch({
              type: 'AUTH/CREATE_TOKEN_DELAY_START',
              delayName,
              seconds: CONFIRMATION_DELAY_SECONDS - diff,
            })
          }
        }
      }

      check(user.emailConfirmation, 'emailConfirmationDelay')
      check(user.phoneConfirmation, 'phoneConfirmationDelay')

      // force logout to use new email
      if (
        !action.isUserSwitching &&
        state.currentUsername &&
        user.username !== state.currentUsername
      ) {
        action.asyncDispatch(logoutTokenExpired())

        return state
      }

      if (state.autoRefreshUserData && !autoRefreshTimeoutId) {
        autoRefreshTimeoutId = setTimeout(() => {
          action.asyncDispatch(loadUserData())
          autoRefreshTimeoutId = null
        }, 1500)
      }

      const allConfirmed = !!(
        user.emailConfirmation?.isConfirmed && user.phoneConfirmation?.isConfirmed
      )

      return i
        .chain(state)
        .assoc('user', user)
        .assoc('currentUsername', user.username)
        .assoc('userDataLoading', false)
        .assoc('allConfirmed', allConfirmed)
        .value()
    }

    case 'AUTH/GENERATE_SHARE_TOKEN_PENDING':
      return i
        .chain(state)
        .assoc('shareTokenLoading', true)
        .value()

    case 'AUTH/GENERATE_SHARE_TOKEN_REJECTED':
      return i
        .chain(state)
        .assoc('shareTokenLoading', false)
        .value()

    case 'AUTH/GENERATE_SHARE_TOKEN_FULFILLED':
      return i
        .chain(state)
        .assoc('shareToken', action.payload.data.token)
        .assoc('shareTokenLoading', false)
        .value()

    case 'AUTH/SET_AUTH_TOKEN':
      return { ...state, token: action.payload }

    case 'AUTH/REMOVE_AUTH_TOKEN':
      return { ...state, token: null }

    case 'AUTH/GET_TOKEN_FULFILLED':
      return { ...state, token: action.payload.data.token }

    case 'AUTH/NEXT_LOGIN_STEP':
      return i.assocIn(state, ['loginForm', 'step'], state.loginForm.step + 1)

    case 'AUTH/PREV_LOGIN_STEP':
      return i.assocIn(state, ['loginForm', 'step'], state.loginForm.step - 1)

    case 'AUTH/LOGIN_FULFILLED': {
      const { user, expiredAt, token } = action.payload.data

      analytics.identify({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.username,
        addedAt: user.addedAt,
        roles: user.roles,
        contactId: user.contactId,
      })

      analytics.track('User - Login')

      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: {
          data: user,
        },
      })

      return i
        .chain(state)
        .assoc('expiration', expiredAt)
        .assoc('token', token)
        .assoc('loggedOut', false)
        .value()
    }

    case 'AUTH/SWITCH_USER_FULFILLED': {
      const { user, expiredAt, token } = action.payload.data
      const { adminToken, adminUserId } = action.meta

      analytics.identify({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.username,
        addedAt: user.addedAt,
        roles: user.roles,
        contactId: user.contactId,
      })

      analytics.track('User - Login')

      action.asyncDispatch(stopAutoRefresh())

      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: {
          data: user,
        },
        isUserSwitching: true,
      })

      return i
        .chain(state)
        .assoc('expiration', expiredAt)
        .assoc('token', token)
        .assoc('adminToken', adminToken)
        .assoc('adminUserId', adminUserId)
        .assoc('loggedOut', false)
        .value()
    }

    case 'AUTH/REGISTER_FULFILLED': {
      const { user, expiredAt, token } = action.payload.data

      analytics.identify({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.username,
        addedAt: user.addedAt,
        roles: user.roles,
        contactId: user.contactId,
      })

      analytics.track({
        action: 'track',
        event: 'User - Register',
        properties: {
          category: 'User',
          label: 'User - Register',
          value: 0,
        },
      })

      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: {
          data: user,
        },
      })

      return i
        .chain(state)
        .assoc('expiration', expiredAt)
        .assoc('token', token)
        .assoc('loggedOut', false)
        .value()
    }

    case 'AUTH/UPDATE_USER':
      analytics.track('User - Update')
      return { ...state, user: action.payload.user }

    case 'AUTH/PASSWORD_UPDATE_PENDING':
      return i.assoc(state, 'passwordUpdateLoading', true)

    case 'AUTH/PASSWORD_UPDATE_REJECTED':
      return i.assoc(state, 'passwordUpdateLoading', false)

    case 'AUTH/PASSWORD_UPDATE_FULFILLED':
      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: action.payload,
      })

      return i.assoc(state, 'passwordUpdateLoading', false)

    case 'AUTH/LOGOUT':
      analytics.track('User - Logout - Manual')
      analytics.reset()

      action.asyncDispatch(stopDelay('emailConfirmationDelay'))
      action.asyncDispatch(stopDelay('phoneConfirmationDelay'))

      if (autoRefreshTimeoutId) {
        clearTimeout(autoRefreshTimeoutId)
        autoRefreshTimeoutId = null
      }

      return {
        ...state,
        ...initialState,
        loggedOut: true,

        // in order to properly stop related intervals we need to preserve delays too
        emailConfirmationDelay: state.emailConfirmationDelay,
        phoneConfirmationDelay: state.phoneConfirmationDelay,
      }

    case 'AUTH/LOGOUT_TOKEN_EXPIRED':
      analytics.track('User - Logout - Token Expired')
      analytics.reset()

      action.asyncDispatch(stopDelay('emailConfirmationDelay'))
      action.asyncDispatch(stopDelay('phoneConfirmationDelay'))

      if (autoRefreshTimeoutId) {
        clearTimeout(autoRefreshTimeoutId)
        autoRefreshTimeoutId = null
      }

      return {
        ...state,
        ...initialState,
        loggedOut: true,

        // in order to properly stop related intervals we need to preserve delays too
        emailConfirmationDelay: state.emailConfirmationDelay,
        phoneConfirmationDelay: state.phoneConfirmationDelay,
      }

    case 'AUTH/CREATE_TOKEN_DELAY_STOP': {
      const delay = state[action.delayName]

      delay.interval && clearInterval(delay.interval)

      return i.assoc(state, action.delayName, {
        active: false,
        seconds: 0,
        interval: null,
      })
    }

    case 'AUTH/CREATE_TOKEN_DELAY_TICK': {
      const delay = state[action.delayName]

      if (delay.seconds <= 0) {
        action.asyncDispatch(stopDelay(action.delayName))

        return state
      }

      return i.assocIn(state, [action.delayName, 'seconds'], state[action.delayName].seconds - 1)
    }

    case 'AUTH/CREATE_TOKEN_DELAY_START': {
      const delay = state[action.delayName]

      delay.interval && clearInterval(delay.interval)

      return i.assoc(state, action.delayName, {
        active: true,
        seconds: action.seconds || CONFIRMATION_DELAY_SECONDS,
        interval: setInterval(
          () =>
            action.asyncDispatch({
              type: 'AUTH/CREATE_TOKEN_DELAY_TICK',
              delayName: action.delayName,
            }),
          1000
        ),
      })
    }

    case 'AUTH/CREATE_EMAIL_CONFIRMATION_PENDING':
      return i.assoc(state, 'emailConfirmationLoading', true)

    case 'AUTH/CREATE_EMAIL_CONFIRMATION_REJECTED':
      return i.assoc(state, 'emailConfirmationLoading', false)

    case 'AUTH/CREATE_EMAIL_CONFIRMATION_FULFILLED':
      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: action.payload,
      })

      return i.assoc(state, 'emailConfirmationLoading', false)

    case 'AUTH/CREATE_PHONE_CONFIRMATION_PENDING':
      return i.assoc(state, 'phoneConfirmationLoading', true)

    case 'AUTH/CREATE_PHONE_CONFIRMATION_REJECTED':
      return i.assoc(state, 'phoneConfirmationLoading', false)

    case 'AUTH/CREATE_PHONE_CONFIRMATION_FULFILLED':
      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: action.payload,
      })

      return i.assoc(state, 'phoneConfirmationLoading', false)

    case 'AUTH/CONFIRM_EMAIL_PENDING':
      return i.assoc(state, 'emailVerifying', true)

    case 'AUTH/CONFIRM_EMAIL_REJECTED':
      return i.assoc(state, 'emailVerifying', false)

    case 'AUTH/CONFIRM_EMAIL_FULFILLED':
      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: action.payload,
      })

      return i.assoc(state, 'emailVerifying', false)

    case 'AUTH/CONFIRM_PHONE_PENDING':
      return i.assoc(state, 'phoneVerifying', true)

    case 'AUTH/CONFIRM_PHONE_REJECTED':
      return i.assoc(state, 'phoneVerifying', false)

    case 'AUTH/CONFIRM_PHONE_FULFILLED':
      action.asyncDispatch({
        type: 'AUTH/LOAD_USER_DATA_FULFILLED',
        payload: action.payload,
      })

      return i.assoc(state, 'phoneVerifying', false)

    case 'AUTH/AUTO_REFRESH_USER_DATA_START':
      if (!state.userDataLoading) {
        action.asyncDispatch(loadUserData())
      }

      return i.assoc(state, 'autoRefreshUserData', true)

    case 'AUTH/AUTO_REFRESH_USER_DATA_STOP':
      return i.assoc(state, 'autoRefreshUserData', false)

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./~/@pickle/pickle-shared/src/features/Auth/Auth.reducer.js